/**
 * My Football Store storefront behaviour, layered on top of Horizon.
 * Plain custom elements; Horizon's dialog-component, theme-drawer (cart) and
 * search modal are reused through their public methods and events.
 */
import { formatMoney } from '@theme/money-formatting';

const MFS = window.MFS || { strings: {}, routes: {} };
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------------------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------------------ */

/**
 * Formats cents with the shop's money format, dropping ".00" like `money_without_trailing_zeros`.
 * @param {number} cents
 */
function money(cents) {
  let format = MFS.moneyFormat || '{{amount}}';
  if (cents % 100 === 0) format = format.replace(/\{\{\s*amount\s*\}\}/, '{{amount_no_decimals}}');
  try {
    return formatMoney(cents, format, MFS.currency || 'INR');
  } catch (error) {
    return String(cents / 100);
  }
}

/** Loads Shopify's standard cart events lazily so a CDN hiccup never breaks the page. */
let standardEvents;
function loadStandardEvents() {
  if (!standardEvents) standardEvents = import('@shopify/events').catch(() => null);
  return standardEvents;
}

function onEditorEvent(name, handler) {
  if (window.Shopify && window.Shopify.designMode) document.addEventListener(name, handler);
}

function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value == null ? '' : String(value);
  return div.innerHTML;
}

/* ---------------------------------------------------------------------------
 * Sticky offset: keeps sticky bars (Filter / Sort, product info) below the header.
 * ------------------------------------------------------------------------ */

function trackHeaderHeight() {
  const headerGroup = document.getElementById('header-group');
  if (!headerGroup || !document.body.classList.contains('mfs-body')) return;
  const set = () => {
    const groupHeight = headerGroup.offsetHeight;
    const header = headerGroup.querySelector('.mfs-header');
    document.documentElement.style.setProperty('--mfs-sticky-offset', `${groupHeight}px`);
    // Horizon's own sections (search filters, cart) read these; its stock header normally sets them.
    if (!document.querySelector('header-component')) {
      document.body.style.setProperty('--header-group-height', `${groupHeight}px`);
      document.body.style.setProperty('--header-height', `${header ? header.offsetHeight : groupHeight}px`);
    }
  };
  set();
  if ('ResizeObserver' in window) new ResizeObserver(set).observe(headerGroup);
}

/* ---------------------------------------------------------------------------
 * Promo bar rotator
 * ------------------------------------------------------------------------ */

class MfsPromoRotator extends HTMLElement {
  connectedCallback() {
    this.messages = [...this.querySelectorAll('.mfs-promo__message')];
    this.index = 0;
    this.interval = Number(this.dataset.interval) || 4000;
    if (this.messages.length < 2) return;
    this.start();
    this.addEventListener('mouseenter', this.stop);
    this.addEventListener('mouseleave', this.start);
    this.onBlockSelect = (event) => {
      const i = this.messages.findIndex((m) => m.contains(event.target));
      if (i < 0) return;
      this.stop();
      this.show(i);
    };
    onEditorEvent('shopify:block:select', this.onBlockSelect);
  }

  disconnectedCallback() {
    this.stop();
    if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  start = () => {
    this.stop();
    if (reducedMotion.matches) return;
    this.timer = window.setInterval(() => this.show((this.index + 1) % this.messages.length), this.interval);
  };

  stop = () => {
    window.clearInterval(this.timer);
  };

  show(i) {
    this.messages.forEach((m, n) => (m.hidden = n !== i));
    this.index = i;
  }
}

/* ---------------------------------------------------------------------------
 * Hero slider (scroll-snap based, swipeable)
 * ------------------------------------------------------------------------ */

class MfsSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-mfs-slider-track]');
    this.slides = [...this.querySelectorAll('[data-mfs-slide]')];
    this.dots = [...this.querySelectorAll('[data-mfs-slider-dot]')];
    this.counter = this.querySelector('[data-mfs-slider-current]');
    this.index = 0;
    if (!this.track || this.slides.length < 2) return;

    this.dots.forEach((dot, i) =>
      dot.addEventListener('click', () => {
        this.goTo(i);
        this.restart();
      })
    );

    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.interval = Number(this.dataset.autoplay) || 0;
    if (this.interval) {
      this.addEventListener('pointerenter', this.pause);
      this.addEventListener('pointerleave', this.play);
      this.addEventListener('focusin', this.pause);
      this.addEventListener('focusout', this.play);
      this.track.addEventListener('touchstart', this.pause, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibility);
      this.play();
    }

    this.onBlockSelect = (event) => {
      const i = this.slides.findIndex((s) => s === event.target || s.contains(event.target));
      if (i < 0) return;
      this.pause();
      this.goTo(i, 'instant');
    };
    onEditorEvent('shopify:block:select', this.onBlockSelect);
  }

  disconnectedCallback() {
    this.pause();
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  onScroll = () => {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      const width = this.track.clientWidth || 1;
      const i = Math.round(Math.abs(this.track.scrollLeft) / width);
      this.setActive(Math.min(Math.max(i, 0), this.slides.length - 1));
    });
  };

  onVisibility = () => (document.hidden ? this.pause() : this.play());

  setActive(i) {
    if (i === this.index && this.dataset.ready) return;
    this.index = i;
    this.dataset.ready = 'true';
    this.dots.forEach((dot, n) => dot.setAttribute('aria-current', n === i ? 'true' : 'false'));
    if (this.counter) this.counter.textContent = String(i + 1).padStart(2, '0');
  }

  goTo(i, behavior = 'smooth') {
    const left = i * this.track.clientWidth;
    this.track.scrollTo({ left, behavior: reducedMotion.matches ? 'instant' : behavior });
    this.setActive(i);
  }

  play = () => {
    if (!this.interval || reducedMotion.matches) return;
    this.pause();
    this.timer = window.setInterval(() => this.goTo((this.index + 1) % this.slides.length), this.interval);
  };

  pause = () => window.clearInterval(this.timer);

  restart() {
    if (this.interval) this.play();
  }
}

/* ---------------------------------------------------------------------------
 * Cart count (header bag + tab bar)
 * ------------------------------------------------------------------------ */

class MfsCartCount extends HTMLElement {
  connectedCallback() {
    loadStandardEvents().then((events) => {
      if (!events || !this.isConnected) return;
      this.eventName = events.StandardEvents.cartLinesUpdate;
      document.addEventListener(this.eventName, this.onUpdate);
    });
    window.addEventListener('pageshow', this.onPageShow);
  }

  disconnectedCallback() {
    if (this.eventName) document.removeEventListener(this.eventName, this.onUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  onUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => this.render(cart?.totalQuantity ?? detail?.itemCount ?? 0))
      .catch(() => {});
  };

  onPageShow = (event) => {
    if (!event.persisted) return;
    fetch(`${MFS.routes.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => this.render(cart.item_count))
      .catch(() => {});
  };

  render(count) {
    this.textContent = count > 0 ? (count > 99 ? '99+' : String(count)) : '';
  }
}

/* ---------------------------------------------------------------------------
 * Wishlist (stored in the shopper's browser)
 * ------------------------------------------------------------------------ */

const WISHLIST_KEY = 'mfs-wishlist';
const wishlist = {
  read() {
    try {
      const value = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (error) {
      return [];
    }
  },
  write(list) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    } catch (error) {
      /* storage unavailable: keep the in-page state only */
    }
    document.dispatchEvent(new CustomEvent('mfs:wishlist', { detail: { list } }));
  },
  has(handle) {
    return this.read().includes(handle);
  },
  toggle(handle) {
    const list = this.read();
    const i = list.indexOf(handle);
    if (i >= 0) list.splice(i, 1);
    else list.unshift(handle);
    this.write(list);
    return i < 0;
  },
};

function syncWishlistButtons(root = document) {
  const list = wishlist.read();
  root.querySelectorAll('[data-mfs-wishlist]').forEach((button) => {
    button.setAttribute('aria-pressed', list.includes(button.dataset.mfsWishlist) ? 'true' : 'false');
  });
}

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-mfs-wishlist]') : null;
  if (!button) return;
  event.preventDefault();
  event.stopPropagation();
  wishlist.toggle(button.dataset.mfsWishlist);
});

document.addEventListener('mfs:wishlist', () => syncWishlistButtons());

class MfsWishlistCount extends HTMLElement {
  connectedCallback() {
    this.render();
    document.addEventListener('mfs:wishlist', this.render);
  }

  disconnectedCallback() {
    document.removeEventListener('mfs:wishlist', this.render);
  }

  render = () => {
    const count = wishlist.read().length;
    this.textContent = count > 0 ? String(count) : '';
  };
}

class MfsWishlistGrid extends HTMLElement {
  connectedCallback() {
    this.render();
    document.addEventListener('mfs:wishlist', this.onChange);
  }

  disconnectedCallback() {
    document.removeEventListener('mfs:wishlist', this.onChange);
  }

  onChange = () => {
    // Remove cards that were un-saved instead of refetching everything.
    const list = wishlist.read();
    this.querySelectorAll('[data-mfs-wishlist-item]').forEach((item) => {
      if (!list.includes(item.dataset.mfsWishlistItem)) item.remove();
    });
    this.toggleEmpty(list.length === 0);
  };

  toggleEmpty(isEmpty) {
    const empty = this.parentElement?.querySelector('[data-mfs-wishlist-empty]');
    if (empty) empty.hidden = !isEmpty;
  }

  async render() {
    const handles = wishlist.read();
    this.toggleEmpty(handles.length === 0);
    const root = (MFS.routes.root || '/').replace(/\/$/, '');
    const cards = await Promise.all(
      handles.map((handle) =>
        fetch(`${root}/products/${encodeURIComponent(handle)}?section_id=mfs-card-render`)
          .then((r) => (r.ok ? r.text() : ''))
          .then((html) => ({ handle, html }))
          .catch(() => ({ handle, html: '' }))
      )
    );
    const parser = new DOMParser();
    this.replaceChildren();
    cards.forEach(({ handle, html }) => {
      const card = html && parser.parseFromString(html, 'text/html').querySelector('.mfs-card');
      if (!card) return;
      const wrapper = document.createElement('div');
      wrapper.dataset.mfsWishlistItem = handle;
      wrapper.append(card);
      this.append(wrapper);
    });
    syncWishlistButtons(this);
  }
}

/* ---------------------------------------------------------------------------
 * New arrivals: team chips + Fan / Player switch
 * ------------------------------------------------------------------------ */

class MfsProductFilter extends HTMLElement {
  connectedCallback() {
    this.team = '';
    this.version = 'fan';
    this.limit = Number(this.dataset.limit) || 6;
    this.cards = [...this.querySelectorAll('[data-mfs-grid] > .mfs-card')];
    this.cards.forEach((card) => {
      const price = card.querySelector('[data-mfs-card-price]');
      if (price) card.fanPriceHtml = price.innerHTML;
    });

    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const chip = target?.closest('[data-mfs-team]');
      const tab = target?.closest('[data-mfs-version]');
      if (chip) {
        this.team = chip.dataset.mfsTeam || '';
        this.querySelectorAll('[data-mfs-team]').forEach((c) =>
          c.setAttribute('aria-pressed', c === chip ? 'true' : 'false')
        );
        this.apply();
      } else if (tab) {
        this.version = tab.dataset.mfsVersion || 'fan';
        this.querySelectorAll('[data-mfs-version]').forEach((t) =>
          t.setAttribute('aria-selected', t === tab ? 'true' : 'false')
        );
        this.apply();
      }
    });

    this.apply();
  }

  apply() {
    let shown = 0;
    const player = this.version === 'player';
    this.cards.forEach((card) => {
      const hasVersions = card.hasAttribute('data-mfs-versions');
      const teamMatch = !this.team || (card.dataset.team || '').toLowerCase() === this.team;
      const versionMatch = !player || hasVersions;
      const visible = teamMatch && versionMatch && shown < this.limit;
      card.hidden = !visible;
      if (visible) shown += 1;

      if (!hasVersions) return;
      const price = card.querySelector('[data-mfs-card-price]');
      const link = card.querySelector('[data-mfs-card-link]');
      const badge = card.querySelector('[data-mfs-card-badge]');
      const playerBadge = card.querySelector('[data-mfs-player-badge]');
      if (price) {
        price.innerHTML = player
          ? `<span class="mfs-price"><span class="mfs-price__current">${escapeHtml(card.dataset.pricePlayer)}</span></span>`
          : card.fanPriceHtml;
      }
      if (link) link.href = player ? card.dataset.urlPlayer : card.dataset.urlFan;
      if (badge) badge.hidden = player;
      if (playerBadge) playerBadge.hidden = !player;
    });

    const count = this.querySelector('[data-mfs-count]');
    if (count) {
      count.textContent =
        shown === 1 ? this.dataset.countOne : (this.dataset.countOther || '').replace('__n__', String(shown));
    }
    const empty = this.querySelector('[data-mfs-empty]');
    if (empty) empty.hidden = shown > 0;
  }
}

/* ---------------------------------------------------------------------------
 * Clubs & countries search
 * ------------------------------------------------------------------------ */

class MfsListFilter extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-mfs-filter-input]');
    this.input?.addEventListener('input', this.filter);
  }

  filter = () => {
    const query = (this.input?.value || '').trim().toLowerCase();
    let any = false;
    this.querySelectorAll('[data-mfs-filter-group]').forEach((group) => {
      let groupHasMatch = false;
      group.querySelectorAll('[data-mfs-filter-item]').forEach((item) => {
        const match = !query || item.dataset.mfsFilterItem.includes(query);
        item.hidden = !match;
        if (match) groupHasMatch = true;
      });
      group.hidden = !groupHasMatch;
      if (groupHasMatch) any = true;
    });
    const empty = this.querySelector('[data-mfs-filter-empty]');
    if (empty) empty.hidden = any;
  };
}

/* ---------------------------------------------------------------------------
 * Collection: live result count in the filter sheet, auto-submit sort, load more
 * ------------------------------------------------------------------------ */

class MfsFilterForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('change', this.onChange);
    this.form?.addEventListener('input', this.onChange);
  }

  onChange = () => {
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.updateCount(), 300);
  };

  async updateCount() {
    if (!this.form) return;
    const params = new URLSearchParams();
    new FormData(this.form).forEach((value, key) => {
      if (value !== '') params.append(key, String(value));
    });
    params.set('section_id', this.dataset.sectionId || '');
    const button = this.closest('dialog')?.querySelector('[data-mfs-filter-submit]');
    this.abort?.abort();
    this.abort = new AbortController();
    try {
      const response = await fetch(`${this.dataset.url}?${params.toString()}`, { signal: this.abort.signal });
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const count = doc.querySelector('[data-mfs-results-count]')?.getAttribute('data-mfs-results-count');
      if (button && count != null) {
        button.textContent = (MFS.strings.results || '__count__').replace('__count__', count);
      }
    } catch (error) {
      /* aborted or offline: keep the previous count */
    }
  }
}

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLSelectElement)) return;
  const form = target.form;
  if (form && form.hasAttribute('data-mfs-autosubmit')) form.submit();
});

document.addEventListener('click', async (event) => {
  const link = event.target instanceof Element ? event.target.closest('[data-mfs-load-more]') : null;
  if (!(link instanceof HTMLAnchorElement)) return;
  const plp = link.closest('[data-mfs-plp]');
  const grid = plp?.querySelector('[data-mfs-grid-items]');
  if (!plp || !grid) return;
  event.preventDefault();
  link.setAttribute('aria-disabled', 'true');
  try {
    const url = new URL(link.href, window.location.origin);
    url.searchParams.set('section_id', plp.getAttribute('data-section-id') || '');
    const html = await (await fetch(url.toString())).text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('[data-mfs-grid-items] > .mfs-card').forEach((card) => grid.append(card));
    const nextEnd = doc.querySelector('[data-mfs-plp-end]');
    const end = plp.querySelector('[data-mfs-plp-end]');
    if (nextEnd && end) end.innerHTML = nextEnd.innerHTML;
    syncWishlistButtons(grid);
  } catch (error) {
    window.location.href = link.href;
  }
});

/* ---------------------------------------------------------------------------
 * Tabs (size guide)
 * ------------------------------------------------------------------------ */

class MfsTabs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
      if (!tab || !this.contains(tab)) return;
      this.querySelectorAll('[role="tab"]').forEach((t) => {
        const selected = t === tab;
        t.setAttribute('aria-selected', selected ? 'true' : 'false');
        const panel = this.querySelector(`#${CSS.escape(t.getAttribute('aria-controls') || '')}`);
        if (panel) panel.hidden = !selected;
      });
    });
  }
}

/* ---------------------------------------------------------------------------
 * Product page
 * ------------------------------------------------------------------------ */

class MfsProduct extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-mfs-product-json]');
    if (!json) return;
    try {
      this.data = JSON.parse(json.textContent || '{}');
    } catch (error) {
      return;
    }

    this.form = this.querySelector('form[data-mfs-form]');
    this.idInput = this.querySelector('[data-mfs-variant-id]');
    this.addButton = this.querySelector('[data-mfs-add]');
    this.addLabel = this.querySelector('[data-mfs-add-label]');
    this.gallery = this.querySelector('[data-mfs-gallery]');
    this.printing = false;

    // If the page was opened without ?variant=, the shopper picks the size.
    if (!this.data.preselect) {
      this.querySelectorAll('[data-mfs-size-option] input[type="radio"]').forEach((input) => (input.checked = false));
    }

    this.form?.addEventListener('change', this.onChange);
    this.form?.addEventListener('input', this.onInput);
    this.form?.addEventListener('submit', this.onSubmit);
    this.gallery?.addEventListener('scroll', this.onGalleryScroll, { passive: true });

    this.update(false);
  }

  get optionFieldsets() {
    return [...this.querySelectorAll('[data-mfs-option]')];
  }

  selectedOptions() {
    const count = this.data.variants[0]?.options.length || 0;
    const selected = new Array(count).fill(null);
    this.optionFieldsets.forEach((fieldset) => {
      const index = Number(fieldset.getAttribute('data-mfs-option'));
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      selected[index] = checked ? checked.value : null;
    });
    return selected;
  }

  matches(variant, selected, skip = -1) {
    return variant.options.every((value, i) => i === skip || selected[i] === null || selected[i] === value);
  }

  onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.hasAttribute('data-mfs-print-toggle')) {
      this.setPrinting(target.value === 'on');
      return;
    }
    if (target.name.startsWith('mfs-option-')) {
      if (target.closest('[data-mfs-size-option]')) this.toggleSizeError(false);
      this.update(true);
    }
  };

  onInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.dataset.mfsPrintInput) return;
    if (target.dataset.mfsPrintInput === 'name') {
      const max = Number(target.dataset.max) || 12;
      target.value = target.value.replace(/[^a-zA-Z .'-]/g, '').slice(0, max);
    } else {
      target.value = target.value.replace(/\D/g, '').slice(0, 2);
    }
    this.querySelector('[data-mfs-print-error]')?.setAttribute('hidden', '');
    this.updatePreview();
  };

  onGalleryScroll = () => {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.updateCounter());
  };

  visibleSlides() {
    return [...(this.gallery?.children || [])].filter((slide) => !slide.hidden);
  }

  updateCounter() {
    const current = this.querySelector('[data-mfs-gallery-current]');
    const total = this.querySelector('[data-mfs-gallery-total]');
    if (!this.gallery || !current) return;
    const slides = this.visibleSlides();
    const i = Math.round(Math.abs(this.gallery.scrollLeft) / (this.gallery.clientWidth || 1));
    current.textContent = String(Math.min(i, slides.length - 1) + 1);
    if (total) total.textContent = String(slides.length);
  }

  scrollToMedia(mediaId) {
    if (!this.gallery || !mediaId) return;
    const slides = this.visibleSlides();
    const i = slides.findIndex((slide) => slide.getAttribute('data-mfs-media-id') === String(mediaId));
    if (i < 0) return;
    this.gallery.scrollTo({ left: i * this.gallery.clientWidth, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
  }

  setPrinting(on) {
    this.printing = on;
    const fields = this.querySelector('[data-mfs-print-fields]');
    if (fields) fields.hidden = !on;
    this.querySelectorAll('[data-mfs-print-input]').forEach((input) => (input.disabled = !on));
    const preview = this.querySelector('[data-mfs-print-preview]');
    if (preview) {
      preview.hidden = !on;
      if (on && this.gallery) this.gallery.scrollTo({ left: 0, behavior: 'instant' });
    }
    this.updatePreview();
    this.updateCounter();
    this.update(false);
  }

  updatePreview() {
    const name = this.querySelector('[data-mfs-print-input="name"]');
    const number = this.querySelector('[data-mfs-print-input="number"]');
    const nameOut = this.querySelector('[data-mfs-print-name]');
    const numberOut = this.querySelector('[data-mfs-print-number]');
    if (nameOut) nameOut.textContent = (name?.value || nameOut.dataset.placeholder || nameOut.textContent).toUpperCase();
    if (nameOut && !nameOut.dataset.placeholder) nameOut.dataset.placeholder = nameOut.textContent;
    if (numberOut) numberOut.textContent = number?.value || '10';
  }

  update(fromUser) {
    const { variants, versionIndex, sizeIndex } = this.data;
    const selected = this.selectedOptions();

    // Availability of each option value, given the other selections.
    this.optionFieldsets.forEach((fieldset) => {
      const index = Number(fieldset.getAttribute('data-mfs-option'));
      if (index === versionIndex) return;
      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        const candidate = [...selected];
        candidate[index] = input.value;
        const available = variants.some((v) => v.available && this.matches(v, candidate));
        input.closest('.mfs-size')?.classList.toggle('is-unavailable', !available);
      });
    });

    const complete = selected.every((value) => value !== null);
    const variant = complete ? variants.find((v) => this.matches(v, selected)) : null;
    this.variant = variant || null;

    // Reference variant for price display before a size is chosen.
    const pool = variants.filter((v) => this.matches(v, selected));
    const reference =
      variant ||
      pool.filter((v) => v.available).sort((a, b) => a.price - b.price)[0] ||
      pool.sort((a, b) => a.price - b.price)[0] ||
      variants[0];

    if (this.idInput) {
      this.idInput.value = variant ? String(variant.id) : '';
      this.idInput.disabled = !variant;
    }

    this.renderPrice(reference);

    // Fit note follows the version.
    const note = this.querySelector('[data-mfs-fit-note]');
    if (note && versionIndex >= 0) {
      const version = (selected[versionIndex] || '').toLowerCase();
      note.textContent = (this.data.fitNotes || {})[version] || this.data.defaultFitNote || '';
    }

    // Buy bar summary.
    const printPrice = this.printing && this.data.printing ? this.data.printing.price : 0;
    const label = this.querySelector('[data-mfs-summary-label]');
    const total = this.querySelector('[data-mfs-total]');
    const size = sizeIndex >= 0 ? selected[sizeIndex] : null;
    if (label) {
      if (size) {
        label.textContent = `${MFS.strings.size || 'Size'} ${size}${this.printing ? ` · ${MFS.strings.printed || 'printed'}` : ''}`;
      } else if (this.printing && this.data.printing) {
        label.textContent = `${MFS.strings.printing || 'Printing'} +${money(this.data.printing.price)}`;
      } else {
        label.textContent = versionIndex >= 0 ? selected[versionIndex] || '' : '';
      }
    }
    if (total && reference) total.textContent = money(reference.price + printPrice);

    // Button state.
    const soldOut = variant ? !variant.available : !pool.some((v) => v.available);
    if (this.addButton) this.addButton.disabled = soldOut;
    if (this.addLabel) {
      this.addLabel.textContent = soldOut ? MFS.strings.soldOut || 'Sold out' : MFS.strings.addToBag || 'Add to bag';
    }

    if (fromUser && variant) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url.toString());
      if (variant.mediaId) this.scrollToMedia(variant.mediaId);
    }
  }

  renderPrice(variant) {
    const holder = this.querySelector('[data-mfs-price]');
    if (!holder || !variant) return;
    const sale = variant.compareAtPrice > variant.price;
    let html = `<span class="mfs-price mfs-price--lg${sale ? ' mfs-price--sale' : ''}"><span class="mfs-price__current">${money(variant.price)}</span>`;
    if (sale) {
      const pct = Math.round(((variant.compareAtPrice - variant.price) * 100) / variant.compareAtPrice);
      html += `<s class="mfs-price__was">${money(variant.compareAtPrice)}</s>`;
      html += `<span class="mfs-price__save">${escapeHtml((MFS.strings.savePercent || 'Save __p__%').replace('__p__', String(pct)))}</span>`;
    }
    holder.innerHTML = `${html}</span>`;
  }

  toggleSizeError(show) {
    const error = this.querySelector('[data-mfs-size-error]');
    if (error) error.hidden = !show;
  }

  onSubmit = async (event) => {
    event.preventDefault();
    if (!this.variant) {
      this.toggleSizeError(true);
      const fieldset = this.querySelector('[data-mfs-size-option]');
      fieldset?.scrollIntoView({ behavior: reducedMotion.matches ? 'instant' : 'smooth', block: 'center' });
      fieldset?.querySelector('input:not(:disabled)')?.focus({ preventScroll: true });
      return;
    }

    const properties = {};
    if (this.printing && this.data.printing) {
      const nameInput = this.querySelector('[data-mfs-print-input="name"]');
      const numberInput = this.querySelector('[data-mfs-print-input="number"]');
      const name = (nameInput?.value || '').trim().toUpperCase();
      const number = (numberInput?.value || '').trim();
      if (!name && !number) {
        this.querySelector('[data-mfs-print-error]')?.removeAttribute('hidden');
        nameInput?.focus();
        return;
      }
      if (name && nameInput) properties[nameInput.name.replace(/^properties\[(.*)\]$/, '$1')] = name;
      if (number && numberInput) properties[numberInput.name.replace(/^properties\[(.*)\]$/, '$1')] = number;
    }

    const items = [{ id: this.variant.id, quantity: 1, properties }];
    if (this.printing && this.data.printing) {
      items.push({
        id: this.data.printing.variantId,
        quantity: 1,
        properties: { _for: `${this.data.title} / ${this.variant.title}` },
      });
    }

    this.addButton?.setAttribute('aria-busy', 'true');
    if (this.addButton) this.addButton.disabled = true;
    try {
      await this.addToCart(items);
      const size = this.data.sizeIndex >= 0 ? this.variant.options[this.data.sizeIndex] : '';
      this.toast(`${MFS.strings.addedToBag || 'Added to bag'}${size ? ` · ${MFS.strings.size || 'Size'} ${size}` : ''}`);
    } catch (error) {
      this.toast(error instanceof Error && error.message ? error.message : MFS.strings.addError, true);
    } finally {
      this.addButton?.removeAttribute('aria-busy');
      this.update(false);
    }
  };

  async addToCart(items) {
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);
    const events = await loadStandardEvents();
    const LinesEvent = events?.CartLinesUpdateEvent;
    const deferred = LinesEvent ? LinesEvent.createPromise() : null;
    if (LinesEvent && deferred) {
      this.dispatchEvent(
        new LinesEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
          promise: deferred.promise,
        })
      );
    }

    const fetchCart = () =>
      fetch(`${MFS.routes.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const baseDetail = {
      source: 'mfs-product-form',
      sourceId: this.dataset.sectionId || '',
      itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
      productId: String(this.data.id),
    };

    try {
      const response = await fetch(MFS.routes.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();

      if (result.status) {
        const cart = await fetchCart().catch(() => null);
        deferred?.resolve({
          cart: cart && LinesEvent ? LinesEvent.createCartFromAjaxResponse(cart) : undefined,
          detail: { ...baseDetail, didError: true, items: cart?.items || [] },
        });
        throw new Error(result.description || result.message || MFS.strings.addError);
      }

      const cart = await fetchCart();
      deferred?.resolve({
        cart: LinesEvent ? LinesEvent.createCartFromAjaxResponse(cart) : undefined,
        detail: { ...baseDetail, didError: false, items: cart.items, sections: result.sections },
      });
      return cart;
    } catch (error) {
      deferred?.reject?.(error);
      throw error;
    }
  }

  toast(message, isError = false) {
    const wrapper = this.querySelector('[data-mfs-toast]');
    if (!wrapper) return;
    const toast = wrapper.querySelector('.mfs-toast');
    const text = wrapper.querySelector('[data-mfs-toast-message]');
    const action = wrapper.querySelector('[data-mfs-toast-action]');
    if (text) text.textContent = message;
    toast?.classList.toggle('mfs-toast--error', isError);
    if (action instanceof HTMLElement) action.hidden = isError;
    wrapper.hidden = false;
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => (wrapper.hidden = true), isError ? 6000 : 3500);
  }
}

/* ---------------------------------------------------------------------------
 * Misc: back button, clubs tab state
 * ------------------------------------------------------------------------ */

document.addEventListener('click', (event) => {
  const back = event.target instanceof Element ? event.target.closest('[data-mfs-back]') : null;
  if (!back) return;
  try {
    const referrer = document.referrer ? new URL(document.referrer) : null;
    if (referrer && referrer.origin === window.location.origin && window.history.length > 1) {
      event.preventDefault();
      window.history.back();
    }
  } catch (error) {
    /* fall back to the link */
  }
});

function wireClubsTab() {
  const menu = document.getElementById('mfs-clubs-menu');
  if (!menu) return;
  const buttons = () => document.querySelectorAll('[aria-controls="mfs-clubs-dialog"]');
  menu.addEventListener('dialog:open', () => buttons().forEach((b) => b.setAttribute('aria-expanded', 'true')));
  menu.addEventListener('dialog:close', () => buttons().forEach((b) => b.setAttribute('aria-expanded', 'false')));
}

/* ---------------------------------------------------------------------------
 * Registration
 * ------------------------------------------------------------------------ */

const elements = {
  'mfs-promo-rotator': MfsPromoRotator,
  'mfs-slider': MfsSlider,
  'mfs-cart-count': MfsCartCount,
  'mfs-wishlist-count': MfsWishlistCount,
  'mfs-wishlist-grid': MfsWishlistGrid,
  'mfs-product-filter': MfsProductFilter,
  'mfs-list-filter': MfsListFilter,
  'mfs-filter-form': MfsFilterForm,
  'mfs-tabs': MfsTabs,
  'mfs-product': MfsProduct,
};

Object.entries(elements).forEach(([name, constructor]) => {
  if (!customElements.get(name)) customElements.define(name, constructor);
});

trackHeaderHeight();
wireClubsTab();
syncWishlistButtons();
document.addEventListener('shopify:section:load', () => {
  syncWishlistButtons();
  wireClubsTab();
});
